import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import momentTimezone from 'moment-timezone';
import {
  Alert,
  Col,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Typography,
} from 'antd';
// Api
import { GET_TICKET_VENUES } from 'api/ticketing/queries';
// Types
import {
  GetTicketVenues,
  GetTicketVenuesVariables,
  GetTicketVenues_myTicketVenues_entities,
} from 'api/ticketing/types/GetTicketVenues';
import { TicketEventFormValues, VenueFormValues, VenueMode } from '../../types';
// Helpers
import { formatTimezoneName, formatVenueAddress } from 'pages/Events/helpers';
// Components
import VenuePlaceSearch from '../VenuePlaceSearch/VenuePlaceSearch';
// Styles
import styles from './VenueSection.module.scss';

const { Text } = Typography;

export type SavedVenue = GetTicketVenues_myTicketVenues_entities;

const TIMEZONE_OPTIONS = momentTimezone.tz
  .names()
  .map((name) => ({ value: name, label: formatTimezoneName(name) }));

type VenueSectionProps = {
  form: FormInstance<TicketEventFormValues>;
  hostId?: string;
  venueMode: VenueMode;
  locked?: boolean;
  lockedVenue?: { name: string; address: string; timezone: string } | null;
  onVenuesLoaded?: (venues: SavedVenue[]) => void;
  /** Values set from a place lookup skip the form's change handler. */
  onFieldsFilled?: () => void;
};

const VenueSection = ({
  form,
  hostId,
  venueMode,
  locked,
  lockedVenue,
  onVenuesLoaded,
  onFieldsFilled,
}: VenueSectionProps): JSX.Element => {
  const { data, loading } = useQuery<GetTicketVenues, GetTicketVenuesVariables>(
    GET_TICKET_VENUES,
    {
      variables: { input: { ownerId: hostId, limit: 100 } },
      skip: !hostId,
      fetchPolicy: 'cache-and-network',
      onCompleted: (result) =>
        onVenuesLoaded?.(result?.myTicketVenues.entities || []),
    }
  );

  const venues = useMemo(() => data?.myTicketVenues.entities || [], [data]);

  if (locked && lockedVenue) {
    return (
      <div className={styles.locked}>
        <Text strong>{lockedVenue.name}</Text>
        <Text type="secondary">{lockedVenue.address}</Text>
        <Text type="secondary">Time zone: {lockedVenue.timezone}</Text>
        <Alert
          className={styles.lockedNote}
          type="info"
          showIcon
          message="The venue can't change once tickets have been on sale."
        />
      </div>
    );
  }

  const fillFromPlace = (venue: VenueFormValues): void => {
    form.setFieldsValue({ venue });
    onFieldsFilled?.();
  };

  return (
    <div className={styles.root}>
      <Form.Item name="venueMode">
        <Radio.Group optionType="button" buttonStyle="solid">
          <Radio.Button value={VenueMode.Saved} disabled={!venues.length}>
            Saved venue{venues.length ? ` (${venues.length})` : ''}
          </Radio.Button>
          <Radio.Button value={VenueMode.New}>New venue</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {!hostId && (
        <Text type="secondary" className={styles.hint}>
          Pick the host first to see the venues they already use.
        </Text>
      )}

      {venueMode === VenueMode.Saved ? (
        <Form.Item
          name="venueId"
          label="Saved venue"
          rules={[{ required: true, message: 'Choose a venue' }]}
        >
          <Select
            showSearch
            loading={loading}
            placeholder="Choose one of the host's venues"
            optionFilterProp="label"
            options={venues.map((venue) => ({
              value: venue.id,
              label: `${venue.name} — ${formatVenueAddress(venue)}`,
            }))}
          />
        </Form.Item>
      ) : (
        <>
          <Form.Item
            label="Find the venue"
            extra="Fills in the address, map pin and time zone. You can still edit every field."
          >
            <VenuePlaceSearch onSelect={fillFromPlace} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name={['venue', 'name']}
                label="Venue name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter the venue name',
                  },
                ]}
              >
                <Input maxLength={250} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['venue', 'addressLine1']}
                label="Address"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter the address',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['venue', 'addressLine2']}
                label="Address line 2"
              >
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['venue', 'city']}
                label="City"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter the city',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name={['venue', 'region']}
                label="State / province"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter the state or province',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                name={['venue', 'postalCode']}
                label="ZIP / postal code"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Enter the ZIP code',
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                name={['venue', 'country']}
                label="Country code"
                normalize={(value?: string) => value?.toUpperCase()}
                rules={[
                  { required: true, message: 'Enter the country code' },
                  {
                    pattern: /^[A-Za-z]{2}$/,
                    message: 'Two letters, e.g. US',
                  },
                ]}
              >
                <Input maxLength={2} placeholder="US" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={['venue', 'timezone']}
                label="Time zone"
                extra="Every event time on this page is in this zone."
                rules={[{ required: true, message: 'Choose the time zone' }]}
              >
                <Select
                  showSearch
                  placeholder="e.g. America/New York"
                  optionFilterProp="label"
                  options={TIMEZONE_OPTIONS}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name={['venue', 'latitude']}
                label="Latitude"
                rules={[
                  { required: true, message: 'Find the venue to pin it' },
                  { type: 'number', min: -90, max: 90, message: '-90 to 90' },
                ]}
              >
                <InputNumber className={styles.number} step={0.0001} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name={['venue', 'longitude']}
                label="Longitude"
                rules={[
                  { required: true, message: 'Find the venue to pin it' },
                  {
                    type: 'number',
                    min: -180,
                    max: 180,
                    message: '-180 to 180',
                  },
                ]}
              >
                <InputNumber className={styles.number} step={0.0001} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.venue?.latitude !== next.venue?.latitude ||
              prev.venue?.longitude !== next.venue?.longitude
            }
          >
            {({ getFieldValue }) => {
              const latitude = getFieldValue(['venue', 'latitude']);
              const longitude = getFieldValue(['venue', 'longitude']);

              return latitude !== undefined && longitude !== undefined ? (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Check the pin on Google Maps
                </a>
              ) : null;
            }}
          </Form.Item>
        </>
      )}
    </div>
  );
};

export default VenueSection;
